import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Routes the app in-place when the user taps a background push notification.
 *
 * The push service worker (public/firebase-messaging-sw.js) can't reliably drive
 * navigation itself on iOS standalone PWAs: WindowClient.navigate() silently
 * no-ops there (it only works on clients the SW controls, and the portal page is
 * uncontrolled), which left a tapped notification foregrounding the app but
 * stranded on its last screen (Review 3 Batch C #10). The SW now postMessages the
 * deep link to the focused window instead; this bridge receives that message and
 * hands the path to React Router, which routes regardless of SW control state.
 *
 * The link is the same scheme the push payload / Scriptable widget use
 * (/portal/work?task=<id>), so WorkHome + WorkTasksModule consume it unchanged.
 *
 * Mount once, app-wide, inside the Router (see App.js). It only reacts to our own
 * message type, so it's inert on marketing routes and everywhere else.
 */
function SwNotificationClickBridge() {
	const navigate = useNavigate();

	useEffect(() => {
		if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
			return undefined;
		}
		const onMessage = (event) => {
			const data = event.data;
			if (
				data &&
				data.type === 'cyv-notification-click' &&
				typeof data.link === 'string'
			) {
				navigate(data.link);
			}
		};
		navigator.serviceWorker.addEventListener('message', onMessage);
		return () =>
			navigator.serviceWorker.removeEventListener('message', onMessage);
	}, [navigate]);

	return null;
}

export default SwNotificationClickBridge;
